import { bunja } from 'bunja';
import { atom } from 'jotai';
import { atomWithMutation } from 'jotai-tanstack-query';

import {
  postTradingCompetitionDeregisterApi,
  postTradingCompetitionRegisterApi,
} from '@flipster/apis/v1/tradingCompetition';
import { overlayBunja } from '@flipster/overlay';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { RouterScope } from '@flipster/react/providers/route';
import { checkAxiosError } from '@flipster/utils/axios';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { gotoFlipster } from '@flipster/utils/env-helper';
import { getErrorCode, getErrorMessage } from '@flipster/utils/error';
import segment from '@flipster/utils/segment';
import { CompetitionsEvent } from '@flipster/utils/segment/events/competitions';
import webviewHandler from '@flipster/utils/webview-handler';

import { webviewInfoBunja } from '../../shared/webview';
import { InviteCodeModal } from './modal/invite-code-modal';
import { CompetitionIdContext, CompetitionIdScope, competitionApiBunja, competitionBunja } from './state';

export const registerCompetitionBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const router = bunja.use(RouterScope);
  const overlay = bunja.use(overlayBunja);
  const id = bunja.use(CompetitionIdScope);
  const { refetchCompetition } = bunja.use(competitionBunja);
  const { competitionAtoms } = bunja.use(competitionApiBunja);
  const { webviewAtom } = bunja.use(webviewInfoBunja);

  const needInvitationCodeAtom = atom((get) => get(competitionAtoms.dataAtom)?.needInvitationCode);

  const registerCompetitionMutationAtom = atomWithMutation<unknown, { invitationCode?: string }, Error>(() => ({
    mutationFn: ({ invitationCode }: { invitationCode?: string }) =>
      postTradingCompetitionRegisterApi(id, { invitationCode }),
    onSuccess: () => {
      refetchCompetition();
      snackbar.success({ message: 'You have successfully registered.' });
    },
    onError: (err) => {
      if (!checkAxiosError(err)) return;
      const errorCode = getErrorCode(err);
      const message = getErrorMessage(err);

      switch (errorCode) {
        case 'api.unauthorized':
          webviewHandler.postMessage({ action: 'link', params: { page: 'login' } }, () => router.push('/login'));
          break;
        case 'api.tradingCompetition.insufficientKYCLevel':
          dialog.open({
            title: { label: 'Notice' },
            content: message,
            buttons: {
              primary: (
                <Dialog.Button
                  hierarchy='primary'
                  label='Verify'
                  onClick={() => {
                    const path = '/user/kyc';
                    if (store.get(webviewAtom)) gotoFlipster(path);
                    else router.push(path);
                  }}
                />
              ),
              secondary: (
                <Dialog.Button
                  hierarchy='secondary'
                  label='I’m OK for now'
                  onClick={() => {
                    dialog.close();
                  }}
                />
              ),
            },
          });
          break;
        case 'api.tradingCompetition.exceededMaxRegistration':
          dialog.open({
            title: { label: 'Notice' },
            content: message,
            buttons: {
              primary: (
                <Dialog.Button
                  hierarchy='primary'
                  label='Confirm'
                  onClick={() => {
                    dialog.close();
                  }}
                />
              ),
            },
          });
          break;
        default:
          snackbar.error({ message: message });
          break;
      }
    },
  }));

  const registerCompetitionErrorAtom = atom((get) => get(registerCompetitionMutationAtom).error);
  const registerCompetitionErrorMessageAtom = atom((get) => {
    const error = get(registerCompetitionErrorAtom);
    if (!error) return;
    return getErrorMessage(error);
  });

  function resetRegisterCompetitionError() {
    store.get(registerCompetitionMutationAtom).reset();
  }

  const deregisterCompetitionMutationAtom = atomWithMutation(() => ({
    mutationFn: () => postTradingCompetitionDeregisterApi(id),
    onSuccess: () => {
      refetchCompetition();
    },
    onError: (err) => {
      if (!checkAxiosError(err)) return;
      const message = getErrorMessage(err);
      snackbar.error({ message: message });
    },
  }));

  async function registerCompetition(invitationCode?: string) {
    const { mutateAsync, isPending } = store.get(registerCompetitionMutationAtom);
    if (isPending) return;
    segment.track(CompetitionsEvent.CompetitionRegisterClick, { competition_id: id });
    if (store.get(needInvitationCodeAtom) && !invitationCode) {
      return overlay.open(({ isOpen, close, unmount }) => (
        <CompetitionIdContext value={id}>
          <InviteCodeModal isOpen={isOpen} onClose={close} onExit={unmount} />
        </CompetitionIdContext>
      ));
    }
    await mutateAsync({ invitationCode });
  }

  async function deregisterCompetition() {
    const { mutateAsync, isPending } = store.get(deregisterCompetitionMutationAtom);
    if (isPending) return;
    await mutateAsync();
  }

  return {
    registerCompetition,
    deregisterCompetition,
    registerCompetitionErrorMessageAtom,
    resetRegisterCompetitionError,
  };
});
registerCompetitionBunja.debugLabel = 'registerCompetitionBunja';
